import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple, ArrowRight, FileXls } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STATE = {
  ok:       { label: "Syncing",   pill: "bg-emerald-500 text-white", border: "border-emerald-400" },
  stale:    { label: "Stale",     pill: "bg-amber-400 text-slate-900", border: "border-amber-400" },
  error:    { label: "Error",     pill: "bg-red-600 text-white", border: "border-red-500" },
  disabled: { label: "Paused",    pill: "bg-slate-400 text-white", border: "border-slate-300" },
  not_set:  { label: "Not linked", pill: "bg-slate-300 text-slate-800", border: "border-slate-300" },
};

const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};

export default function AppFolioSyncCard() {
  const [h, setH] = useState(null);
  useEffect(() => {
    api.get("/appfolio/sync-health").then(r => setH(r.data)).catch(() => setH({ state: "error", last_error: "health check failed" }));
  }, []);
  if (!h) return null;
  const st = STATE[h.state] || STATE.not_set;
  const report = (h.reports || []).find(r => r.kind === "open_work_orders");
  const moves = (h.reports || []).find(r => r.kind === "moves");
  return (
    <div className={`border-2 ${st.border} bg-white p-4 space-y-3`} data-testid="appfolio-sync-card">
      <div className="flex items-start gap-3">
        <EnvelopeSimple weight="fill" size={22} className="text-slate-700 mt-0.5 shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase tracking-widest text-xs text-slate-900">AppFolio Sync</div>
          <div className="text-[11px] text-slate-500 truncate">{h.email || "Gmail not linked"}</div>
        </div>
        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 ${st.pill}`} data-testid="appfolio-sync-state">{st.label}</span>
      </div>

      {h.state === "error" && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 p-2 font-mono break-all" data-testid="appfolio-sync-error">{h.last_error}</div>
      )}
      {h.state === "not_set" && (
        <div className="text-[11px] text-slate-600">New AppFolio WOs won't flow in until Gmail is linked.</div>
      )}

      {h.state !== "not_set" && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="border border-slate-200 p-2">
            <div className="text-xl font-black text-slate-900" data-testid="appfolio-new-wos-7d">{h.new_wos_7d}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">New WOs · 7d</div>
          </div>
          <div className="border border-slate-200 p-2">
            <div className="text-xl font-black text-slate-900">{h.updates_7d}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Updates · 7d</div>
          </div>
          <div className="border border-slate-200 p-2">
            <div className="text-xl font-black text-slate-400">{h.skipped_7d}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Skipped · 7d</div>
          </div>
        </div>
      )}

      {h.state !== "not_set" && (
        <div className="text-[11px] text-slate-600 space-y-0.5">
          <div>Last poll <b className="text-slate-900">{ago(h.last_poll_at)}</b> · last AppFolio email <b className="text-slate-900">{ago(h.last_email_at)}</b></div>
          {report && (
            <div className="flex items-center gap-1" data-testid="appfolio-report-row">
              <FileXls weight="fill" size={12} className="text-emerald-700"/>
              Open WOs report {ago(report.ran_at)}: <b>{report.result?.created_wos ?? 0} new</b>, {report.result?.updated_wos ?? 0} updated of {report.result?.rows ?? 0}
            </div>
          )}
          {moves && (
            <div className="flex items-center gap-1">
              <FileXls weight="fill" size={12} className="text-sky-700"/>
              Moves roster {ago(moves.ran_at)}: <b>{moves.result?.events_new ?? 0} new</b> of {moves.result?.events_seen ?? 0}
            </div>
          )}
        </div>
      )}

      <Link to="/admin/inbox-setup" className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900" data-testid="appfolio-sync-link">
        {h.state === "not_set" ? "Link Gmail" : "Inbox setup & log"} <ArrowRight weight="bold" size={11}/>
      </Link>
    </div>
  );
}
